import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@sw/api-client';
import { AUTH_ME_QUERY_KEY } from '@sw/auth';
import { api } from '../../lib/api-client';
import { GoogleLogo } from './icons';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (cfg: GsiInitOptions) => void;
          renderButton: (parent: HTMLElement, cfg: GsiButtonOptions) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GsiInitOptions {
  client_id: string;
  callback: (response: { credential: string }) => void;
  auto_select?: boolean;
  ux_mode?: 'popup' | 'redirect';
}

interface GsiButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  width?: string | number;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
}

/**
 * Botón de Google Sign-In usando Google Identity Services (GSI) vanilla — sin
 * dependencias adicionales. El id_token devuelto por Google se envía al backend
 * <c>POST /api/client/auth/google</c>.
 *
 * SW-310. Si <c>VITE_GOOGLE_CLIENT_ID</c> no está configurado en el build, el
 * botón se muestra deshabilitado con tooltip "no configurado" (no oculto, para
 * que el equipo de producto vea que la maqueta existe).
 */
export function GoogleSignInButton({
  accessLinkToken,
  disabled,
  onBlocked,
  onAuthenticated,
}: {
  accessLinkToken: string | null;
  disabled?: boolean;
  onBlocked: (email: string, domain: string) => void;
  onAuthenticated: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

  useEffect(() => {
    if (!clientId || !accessLinkToken) return;
    if (document.querySelector(`script[src="${GSI_SRC}"]`)) {
      setScriptLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, [clientId, accessLinkToken]);

  useEffect(() => {
    if (!scriptLoaded || !clientId || !accessLinkToken || !containerRef.current) return;
    const gsi = window.google?.accounts?.id;
    if (!gsi) return;

    gsi.initialize({
      client_id: clientId,
      callback: async (resp) => {
        setBusy(true);
        setError(null);
        try {
          await api.post('/api/client/auth/google', {
            idToken: resp.credential,
            accessLinkToken,
          });
          await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
          onAuthenticated();
        } catch (err) {
          if (err instanceof ApiError && err.status === 403) {
            // Backend devuelve { email, domain } en el body para 403 domain-blocked.
            const body = err.body as { email?: string; domain?: string } | null;
            if (body?.email && body?.domain) {
              onBlocked(body.email, body.domain);
              return;
            }
          }
          setError(t('login.errors.unexpected'));
        } finally {
          setBusy(false);
        }
      },
      ux_mode: 'popup',
    });

    gsi.renderButton(containerRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      width: containerRef.current.offsetWidth || 360,
      text: 'continue_with',
    });
  }, [scriptLoaded, clientId, accessLinkToken, onAuthenticated, onBlocked, queryClient, t]);

  // Fallback visual cuando no está configurado o esperando script.
  if (!clientId) {
    return (
      <Button
        variant="outlined"
        size="large"
        disabled
        startIcon={<GoogleLogo />}
        data-testid="login-google-disabled"
        sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'center', gap: 1 }}
      >
        {t('login.continueWithGoogle')}
      </Button>
    );
  }

  return (
    <Box>
      {!scriptLoaded && (
        <Button
          variant="outlined"
          size="large"
          disabled
          fullWidth
          data-testid="login-google-loading"
          sx={{ textTransform: 'none' }}
        >
          <CircularProgress size={16} sx={{ mr: 1 }} />
          {t('login.continueWithGoogle')}
        </Button>
      )}
      <Box
        ref={containerRef}
        data-testid="login-google-button"
        sx={{
          minHeight: 44,
          opacity: disabled || busy ? 0.5 : 1,
          pointerEvents: disabled || busy ? 'none' : 'auto',
        }}
      />
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="login-google-error">
          {error}
        </Alert>
      )}
    </Box>
  );
}
